interface ArrayExtensions<T> {
  chunk: (size: number) => T[][];
  compact: () => NonNullable<T>[];
  distinct: () => T[];
  distinctBy: (getKey: (item: T) => unknown) => T[];
  except: (itemsToExclude: T[]) => T[];
  exceptBy: (itemsToExclude: T[], getKey: (item: T) => unknown) => T[];
  first: (predicate?: (item: T) => boolean, errorMessage?: string) => T;
  firstOrDefault: (predicate?: (item: T) => boolean) => T | undefined;
  findDuplicates: () => T[];
  groupBy: <K extends PropertyKey>(getKey: (item: T) => K) => Record<K, T[]>;
  hasDuplicates: () => boolean;
  hasDuplicatesBy: (getKey: (item: T) => unknown) => boolean;
  orderBy: (getValues: OrderBySelectors<T>) => T[];
  orderByDescending: (getValues: OrderBySelectors<T>) => T[];
  single: (predicate?: (item: T) => boolean, errorMessage?: string) => T;
  singleOrDefault: (predicate?: (item: T) => boolean) => T | undefined;
  splitBy: (predicate: Predicate<T>) => { passed: T[]; failed: T[] };
}

type OrderByDirection = 'asc' | 'desc';
type OrderByValue = string | number | boolean | Date | null | undefined;
type OrderBySelector<T> = (item: T) => OrderByValue;
type OrderBySelectors<T> = OrderBySelector<T> | OrderBySelector<T>[];
type Predicate<T> = (item: T) => boolean;

export const arrayExtensions = <T>(value: T[]): ArrayExtensions<T> => {
  const chunk = (size: number): T[][] => {
    if (!Number.isInteger(size) || size <= 0) {
      throw new Error('Chunk size must be a positive integer.');
    }

    const chunks: T[][] = [];

    for (let index = 0; index < value.length; index += size) {
      chunks.push(value.slice(index, index + size));
    }

    return chunks;
  };

  const compact = (): NonNullable<T>[] =>
    value.filter((item): item is NonNullable<T> => item !== null && item !== undefined);

  const distinct = (): T[] => Array.from(new Set(value).values());

  const distinctBy = (getKey: (item: T) => unknown): T[] => {
    const distinctKeys = arrayExtensions(value.map(getKey)).distinct();

    return distinctKeys.map(key => arrayExtensions(value).first(item => getKey(item) === key));
  };

  const except = (itemsToExclude: T[]): T[] => {
    const exclusionSet = new Set(itemsToExclude);

    return value.filter(item => !exclusionSet.has(item));
  };

  const exceptBy = (itemsToExclude: T[], getKey: (item: T) => unknown): T[] => {
    const exclusionKeys = new Set(itemsToExclude.map(getKey));

    return value.filter(item => !exclusionKeys.has(getKey(item)));
  };

  const firstOrDefault = (predicate?: Predicate<T>): T | undefined => {
    if (!predicate) {
      return value[0];
    }

    return value.find(predicate);
  };

  const first = (predicate?: Predicate<T>, errorMessage?: string): T => {
    const item = firstOrDefault(predicate);

    if (item === undefined) {
      throw new Error(errorMessage ?? 'Value is missing.');
    }

    return item;
  };

  const findDuplicates = (): T[] => {
    const seen = new Set<T>();
    const duplicates = new Set<T>();

    for (const item of value) {
      if (seen.has(item)) {
        duplicates.add(item);
      } else {
        seen.add(item);
      }
    }

    return Array.from(duplicates);
  };

  const hasDuplicates = (): boolean => new Set(value).size !== value.length;

  const hasDuplicatesBy = (getKey: (item: T) => unknown): boolean => {
    const keys = value.map(getKey);

    return new Set(keys).size !== keys.length;
  };

  const groupBy = <K extends PropertyKey>(getKey: (item: T) => K): Record<K, T[]> =>
    value.reduce(
      (groups, item) => {
        const key = getKey(item);
        groups[key] ??= [];
        groups[key].push(item);

        return groups;
      },
      {} as Record<K, T[]>
    );

  const orderArray = (getValues: OrderBySelectors<T>, direction: OrderByDirection): T[] => {
    const result = [...value];
    if (result.length <= 1) return result;

    const selectors = Array.isArray(getValues) ? getValues : [getValues];
    const criteriaCount = selectors.length;

    return result.sort((a, b) => {
      for (let i = 0; i < criteriaCount; i++) {
        const selector = selectors[i];

        const valA = selector(a);
        const valB = selector(b);

        if (valA === valB) continue;

        const isValANullish = valA === null || valA === undefined;
        const isValBNullish = valB === null || valB === undefined;

        if (isValANullish && isValBNullish) continue;
        if (isValANullish) return 1;
        if (isValBNullish) return -1;

        const compA = valA instanceof Date ? valA.getTime() : valA;
        const compB = valB instanceof Date ? valB.getTime() : valB;

        if (compA > compB) return direction === 'asc' ? 1 : -1;
        if (compA < compB) return direction === 'asc' ? -1 : 1;
      }

      return 0;
    });
  };

  const orderBy = (getValues: OrderBySelectors<T>): T[] => orderArray(getValues, 'asc');

  const orderByDescending = (getValues: OrderBySelectors<T>): T[] => orderArray(getValues, 'desc');

  const singleOrDefault = (predicate?: Predicate<T>): T | undefined => {
    const filtered = predicate ? value.filter(predicate) : value;

    if (filtered.length > 1) {
      throw new Error('Array contains more than one matching element.');
    }

    return filtered[0];
  };

  const single = (predicate?: Predicate<T>, errorMessage?: string): T => {
    const item = singleOrDefault(predicate);

    if (item === undefined) {
      throw new Error(errorMessage ?? 'Value is missing.');
    }

    return item;
  };

  const splitBy = (predicate: Predicate<T>): { passed: T[]; failed: T[] } => {
    const passed: T[] = [];
    const failed: T[] = [];

    value.forEach(item => {
      if (predicate(item)) {
        passed.push(item);

        return;
      }

      failed.push(item);
    });

    return { passed, failed };
  };

  return {
    chunk,
    compact,
    distinct,
    distinctBy,
    except,
    exceptBy,
    first,
    firstOrDefault,
    findDuplicates,
    groupBy,
    hasDuplicates,
    hasDuplicatesBy,
    orderBy,
    orderByDescending,
    single,
    singleOrDefault,
    splitBy,
  };
};
